import React from 'react';

import Skeleton from '../Skeleton';
import {
  BuildpackLayersIcon,
  DOCKER_LOGO,
  getStackLogo,
  SUPPORTED_STACKS_SENTENCE,
} from './stackLogos';
import './BuildMethodSelector.css';

export type BuildMethod = 'DOCKERFILE' | 'BUILDPACK';

// Floor on the row height, sized to a one-line description — the common case.
// It keeps the skeleton and the settled rows the same height, so the section
// does not jump when the detection lands.
const ROW_CLASSNAME =
  'build-method-row relative flex items-start gap-3.5 rounded-card border p-4 min-h-[4.5rem] transition-colors duration-[120ms]';

interface BuildMethodOptionProps {
  method: BuildMethod;
  title: string;
  description: string;
  // The method's own mark, so the row is recognizable before it is read.
  logo: React.ReactNode;
  // Inline before the title: the detected stack's mark, when there is one.
  titleLogo?: React.ReactNode;
  // Slot under the title: the reason the option is disabled, or nothing.
  subtitle?: React.ReactNode;
  selected: boolean;
  disabled: boolean;
  groupName: string;
  onSelect: (method: BuildMethod) => void;
}

// Selectable row: the whole row is the control. The radio is the real input,
// visually hidden (sr-only, NOT display:none, so it stays focusable and arrow
// keys still move through the group) with the dot drawn in CSS — same 16px
// ring as RadioButton, which is the radio the rest of the dashboard uses. The
// focus ring is drawn on the row.
const BuildMethodOption = ({
  method,
  title,
  description,
  logo,
  titleLogo,
  subtitle,
  selected,
  disabled,
  groupName,
  onSelect,
}: BuildMethodOptionProps) => {
  let stateClassName;

  // Only the chosen one is filled. Two tinted, outlined panes the width of the
  // page, for a choice between two words, were the loudest thing on Build &
  // deploy — and a fill on both says nothing about which one is picked.
  if (disabled) {
    stateClassName = 'border-navy-500 opacity-50 cursor-not-allowed';
  } else if (selected) {
    stateClassName = 'border-action bg-[#152B45] cursor-pointer';
  } else {
    stateClassName = 'border-navy-500 hover:bg-navy-800 cursor-pointer';
  }

  return (
    <label className={`${ROW_CLASSNAME} ${stateClassName}`}>
      <input
        type="radio"
        className="sr-only"
        name={groupName}
        value={method}
        checked={selected}
        disabled={disabled}
        onChange={() => onSelect(method)}
      />
      {/* Sits on the title's baseline, not the row's — the row grows downward
          with the description and the radio should not drift with it. */}
      <span className="build-method-radio mt-0.5" aria-hidden="true" />
      <span className="build-method-logo shrink-0" aria-hidden="true">
        {logo}
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-ds-135 font-semibold text-white">
          {titleLogo && (
            <span className="flex-none flex" aria-hidden="true">
              {titleLogo}
            </span>
          )}
          {title}
        </span>
        {subtitle && <span className="block text-ds-115 text-state-err mt-1">{subtitle}</span>}
        {/* Not on a phone: two options, each two lines of pitch, sat between
            the label and the fields that actually need an answer. */}
        <span className="hidden sm:block text-ds-115 leading-[1.5] text-navy-200 mt-1">{description}</span>
      </span>
    </label>
  );
};

// Placeholder with the same frame and height as a real row, so the section does
// not resize when the detection lands.
const BuildMethodOptionSkeleton = () => (
  <div className={`${ROW_CLASSNAME} border-navy-500`}>
    <Skeleton className="h-4 w-4 rounded-full mt-0.5 shrink-0" />
    <Skeleton className="h-6 w-6 rounded shrink-0" />
    <div className="flex-1">
      <Skeleton className="h-5 w-40 rounded" />
      <Skeleton className="h-3 w-full rounded mt-3" />
    </div>
  </div>
);

interface BuildMethodSelectorProps {
  value: BuildMethod;
  onChange: (value: BuildMethod) => void;
  // A detection is in flight for the current branch + path.
  isDetecting?: boolean;
  // Whether a previous detection already produced an answer. Drives the
  // difference between "nothing to show yet" (skeleton) and "refreshing what
  // is already on screen" (dimmed rows).
  hasDetectionResult?: boolean;
  // Only the create form disables Dockerfile when the repository has none —
  // there is no point in creating an app whose first deploy already fails. In
  // Settings it stays selectable (the user may be about to commit one).
  dockerfileDisabled?: boolean;
  // Shown under the Dockerfile title: why it is disabled, typically.
  dockerfileSubtitle?: React.ReactNode;
  // Shown under the Buildpacks title, for what the title cannot say — that
  // nothing was detected here, typically.
  buildpackSubtitle?: React.ReactNode;
  // The stack the API detected, spelled as `detectStack` returns it. Names the
  // Buildpacks row and picks its logo. Absent in Settings, which never detects.
  detectedStack?: string;
  // The repository has a Dockerfile that this build method will ignore.
  showDockerfileIgnoredWarning?: boolean;
  // The route is off for this environment: the option is not offered at all.
  // Undefined means "no opinion" (Settings, where the app already has a stored
  // Build Method) and keeps both options available.
  buildpacksAvailable?: boolean;
  isDisabled?: boolean;
  groupName?: string;
}

const BuildMethodSelector = ({
  value,
  onChange,
  isDetecting,
  hasDetectionResult,
  dockerfileDisabled,
  dockerfileSubtitle,
  buildpackSubtitle,
  detectedStack,
  showDockerfileIgnoredWarning,
  buildpacksAvailable,
  isDisabled,
  groupName = 'buildMethod',
}: BuildMethodSelectorProps) => {
  // Nothing to choose from: render as it was before the route existed.
  if (buildpacksAvailable === false) {
    return null;
  }

  const onSelect = (method: BuildMethod) => {
    if (isDisabled || (method === 'DOCKERFILE' && dockerfileDisabled)) {
      return;
    }

    onChange(method);
  };

  // The status line keeps its height whether or not it has text, so the rows
  // never move when the detection starts or lands. Callers that never detect
  // (Settings) don't get the line at all.
  const statusLine =
    isDetecting === undefined ? null : (
      // Collapsed on a phone while idle: the reserved height was an empty band
      // under the label, and there the column scrolls anyway.
      <div className={`h-5 text-ds-115 text-navy-200 ${isDetecting ? '' : 'hidden sm:block'}`}>
        {isDetecting ? 'Detecting your app…' : ''}
      </div>
    );

  // Nothing detected yet: show the frames loading instead of rendering a
  // pre-selection that is about to change under the user's eyes.
  if (isDetecting && !hasDetectionResult) {
    return (
      <div className="space-y-4">
        {statusLine}
        <div className="space-y-3" aria-busy="true">
          <BuildMethodOptionSkeleton />
          <BuildMethodOptionSkeleton />
        </div>
      </div>
    );
  }

  const stackLogo = getStackLogo(detectedStack);
  const DockerLogo = DOCKER_LOGO.Logo;

  // The detected stack names the row: "Node.js Buildpack" says what will build
  // this repository, where "Buildpacks" only says how. Without a detection
  // there is no stack to name, so the row stays generic.
  const buildpackTitle = detectedStack
    ? `${detectedStack} Buildpack`
    : 'Buildpacks';

  // Re-detecting keeps the current answer on screen and only dims it — the
  // rows stay where they are.
  return (
    <div className="space-y-4">
      {statusLine}
      <div
        role="radiogroup"
        aria-label="Build Method"
        aria-busy={isDetecting}
        className={`space-y-3 transition-opacity ${
          isDetecting ? 'opacity-60' : ''
        }`}
      >
        <BuildMethodOption
          method="DOCKERFILE"
          title="Dockerfile"
          description="You control the runtime, dependencies and environment."
          logo={
            <DockerLogo
              width="24"
              height="24"
              fill="currentColor"
              style={{ color: DOCKER_LOGO.color }}
            />
          }
          subtitle={dockerfileSubtitle}
          selected={value === 'DOCKERFILE'}
          disabled={!!isDisabled || !!dockerfileDisabled}
          groupName={groupName}
          onSelect={onSelect}
        />
        <BuildMethodOption
          method="BUILDPACK"
          title={buildpackTitle}
          description={
            detectedStack
              ? 'Back4app builds and updates the image for you — no Dockerfile needed.'
              : `Back4app detects your stack and builds it: ${SUPPORTED_STACKS_SENTENCE}.`
          }
          // The layers mark stays in the logo column whatever was detected,
          // the way Docker's does on the row above: that column names the
          // method. The stack is a second fact and sits with the title it
          // already names — swapping one mark for the other left Buildpacks
          // with no icon of its own the moment a stack was found.
          logo={
            <BuildpackLayersIcon
              width="24"
              height="24"
              className="text-navy-200"
            />
          }
          titleLogo={
            stackLogo ? (
              <stackLogo.Logo
                width="16"
                height="16"
                fill="currentColor"
                style={{ color: stackLogo.color }}
              />
            ) : undefined
          }
          subtitle={buildpackSubtitle}
          selected={value === 'BUILDPACK'}
          disabled={!!isDisabled}
          groupName={groupName}
          onSelect={onSelect}
        />
      </div>
      {showDockerfileIgnoredWarning && value === 'BUILDPACK' && (
        <div className="text-ds-115 leading-[1.5] text-navy-200">
          Your Dockerfile won't be used — this app is built with Buildpacks.
        </div>
      )}
    </div>
  );
};

export default BuildMethodSelector;
